import React, { useState, useRef } from "react";
import captureDocButton from "../../assets/captureDocButton.svg";
import { residentIDCardName, drivingLicenceName, idCardName, countries } from "../../constants/countries";
import { documentSideConfigurationConstants } from "../../constants/documentSideConfigConstants";
import Footer from "../../components/Footer";
import GoogleIcon from "../../components/GoogleIcon";
import { languageData } from "../../constants/languageData";
import { getDocumentCountry, getDocumentIdType } from "../../utils/documentFields";

function MobileCapture({
  uploadAllowed,
  color,
  documentSideConfiguration=documentSideConfigurationConstants.FRONT_MANDATORY_BACK_OPTIONAL,
  journey,
  docType,
  isAutoCaptureCameraRunning=true,
  captureMode,
  switchToManual=false,
  captureClick,
  sameSideDocumentFlagNumber=-1,
  uploadClick,
  skipClick,
  isSwitchingMode=false,
  language="en",
  currentDocumentStep=0
}) {
  const t = languageData[language]?.mobileDocCapture ?? languageData["en"]?.mobileDocCapture;
  // This document's own country and type, not the first document's (IDX-677).
  const [countryCode] = useState(() => getDocumentCountry(journey, currentDocumentStep));
  const [countryName] = useState(
    countryCode
      ? countries?.find((country) => country.value === countryCode)?.label
      : null
  );
  const [documentName] = useState(() => getDocumentIdType(journey, currentDocumentStep));
  
  // Ref to track last upload click timestamp for throttling
  const lastUploadClick = useRef(0);
  const buttonStyle = {
    position: "relative",
    width: "70px",
    height: "70px",
    borderRadius: "50%",
    backgroundColor: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
  };

  const innerCircleStyle = {
    position: "absolute",
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    backgroundColor: "#000000",
  };

  const outerRingStyle = {
    position: "absolute",
    width: "64px",
    height: "65px",
    borderRadius: "50%",
    border: "4px solid #000000",
    boxShadow: "0 0 12px rgba(0, 0, 0, 0.2)",
  };

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div
        className="header"
        style={{
          borderBottom: "0.2px solid #DDD",
          width: "100%",
          textAlign: "center",
          paddingBottom: "18px",
          fontSize: 16,
          fontWeight: 600,
          textWrap: "wrap",
          color: color,
        }}
      >
        {journey &&
          (docType === "front" ? (sameSideDocumentFlagNumber==1 ? t?.backSide : t?.frontSide) : t?.backSide) +
            ` ${t?.of} ` +
            (
              documentName == 'Resident Id Card' 
                  ? (residentIDCardName?.[countryCode] ? residentIDCardName?.[countryCode] : "Identity Card")
                  : documentName == 'Passport'
                  ? `${countryName} Passport`
                  : documentName == 'Driving License'
                  ? (drivingLicenceName?.[countryCode] ? drivingLicenceName?.[countryCode] : documentName)
                  : documentName == 'Id Card'
                  ? (idCardName?.[countryCode] ? idCardName?.[countryCode] : documentName)
                  : documentName
            )
          }
      </div>
      <div
        className="mid-section"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          padding: captureMode === "auto" ?"24px 0": "12px 0",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            fontSize: 14,
            paddingRight: 16,
            borderRight: "1px solid #DDD",
            cursor: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "not-allowed" : "pointer",
            color: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "#919191" : "#555555",
            visibility: uploadAllowed ? "visible" : "hidden",
            pointerEvents: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "none" : "auto",
            opacity: (!isAutoCaptureCameraRunning || isSwitchingMode) ? 0.5 : 1
          }}
          onClick={() => {
            if (!isAutoCaptureCameraRunning || isSwitchingMode) return;
            
            // Throttling: Check if 1 second has passed since last click
            const now = Date.now();
            if (now - lastUploadClick.current < 1000) {
              console.log('[MobileCapture] Upload click throttled - please wait 1 second');
              return;
            }
            
            // Update last click timestamp
            lastUploadClick.current = now;
            console.log('[MobileCapture] Upload button clicked');
            uploadClick();
          }}
        >
          <GoogleIcon iconName="upload" size={22} style={{ color: "#555555" }} />
          <div>{t?.upload}</div>
        </div>

        {captureMode === "auto" ? (
          <div
            style={{
              fontSize: 14,
              fontWeight: 500,
              textAlign: "center",
              opacity: (isSwitchingMode) ? 0.5 : 1
            }}
          >
            <div style={{ color: "#919191" }}>{t?.stillNotWorking}</div>
            <div
              style={{
                textDecorationLine: "underline",
                color: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "#919191" : "#555",
                cursor: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "not-allowed" : "pointer",
                pointerEvents: (!isAutoCaptureCameraRunning || isSwitchingMode) ? "none" : "auto"
              }}
              onClick={async () => {
                if (!isAutoCaptureCameraRunning || isSwitchingMode) return;
                try {
                  await switchToManual();
                } catch (error) {
                  console.error("Switch error:", error);
                }
              }}
            >
              {t?.clickToCapture}
            </div>
          </div>
        ) : (
          <div
            onClick={async () => {
              if (isSwitchingMode) return;
              try {
                await captureClick();
              } catch (error) {
                console.error("Capture error:", error);
              }
            }}
            style={{
              cursor: (isSwitchingMode) ? "not-allowed" : "pointer",
              opacity: (isSwitchingMode) ? 0.5 : 1,
              position: 'relative'
            }}
          >
            <div style={buttonStyle}>
              <div style={outerRingStyle}></div>
              <div style={{
                ...innerCircleStyle,
                backgroundColor: (isSwitchingMode) ? "#999" : "#000000"
              }}></div>
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minWidth: 64,
            fontSize: 14,
            paddingLeft: 16,
            borderLeft: (docType === "front" || documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY) ? "none" : "1px solid #DDD",
            cursor: (!isAutoCaptureCameraRunning) ? "not-allowed" : "pointer",
            color: (!isAutoCaptureCameraRunning) ? "#919191" : "#555555",
            pointerEvents: (!isAutoCaptureCameraRunning) ? "none" : "auto"
          }}
          onClick={() => {
            if (!isAutoCaptureCameraRunning || (documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY)) return;
            skipClick();
          }}        
        >
          <GoogleIcon 
            iconName="arrow_forward" 
            size={22} 
            style={{
              color: (!isAutoCaptureCameraRunning) ? "#919191" : "inherit",
              display: "flex",
              visibility: (docType === "front" || documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY) ? "hidden" : "visible"
            }} 
          />
          <div style={{ 
            display: "flex",
            color: (!isAutoCaptureCameraRunning) ? "#919191" : "inherit",
            visibility:  (docType === "front" || documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY) ?  "hidden" : "visible"
          }}>
            {t?.skip}
          </div>
        </div>
      </div>
      <Footer style={{ marginTop: 0 }} />
    </div>
  );
}

export default MobileCapture;
